/**
 * TaskActivityDrawer
 * Right-side drawer aggregating every comment and note across the tasks the
 * current user is involved in.
 *
 * Data lifecycle lives in `<TaskActivitySubscriber />`; this component only
 * renders the store and owns the seen-tracking observer for its scroll area.
 *
 * An Online tab (who currently has Task Management open) is intentionally not
 * mounted for now — the hub still tracks presence and
 * `<TaskActivityOnlineList />` is ready to render when it returns.
 */

import { useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Stack, Group, Text, Button, Badge, Box } from '@/components/ui';
import { Switch } from '@/components/ui/inputs';
import { Drawer } from '@/components/ui/overlays';
import { Tabs } from '@/components/ui/navigation';
import { IconMessage, IconNote, IconChecks } from '@tabler/icons-react';
import { useTaskPortalActivityFeedService } from '@actschurch/ccms-sdk';
import {
  ACTIVITY_TYPE_COMMENT,
  ACTIVITY_TYPE_NOTE,
  useTaskActivityStore,
  type TaskActivityTab,
} from '@/stores/taskActivityStore';
import { useActivitySeenTracker } from '../hooks/useActivitySeenTracker';
import { TaskActivityGroup } from './TaskActivityGroup';

export function TaskActivityDrawer() {
  const navigate = useNavigate();
  const location = useLocation();
  const feedService = useTaskPortalActivityFeedService();
  const scrollRef = useRef<HTMLDivElement>(null);

  const drawerOpen = useTaskActivityStore((s) => s.drawerOpen);
  const setDrawerOpen = useTaskActivityStore((s) => s.setDrawerOpen);
  const activeTab = useTaskActivityStore((s) => s.activeTab);
  const setActiveTab = useTaskActivityStore((s) => s.setActiveTab);
  const groups = useTaskActivityStore((s) => s.groups);
  const unreadComments = useTaskActivityStore((s) => s.unreadComments);
  const unreadNotes = useTaskActivityStore((s) => s.unreadNotes);
  const markSeenLocal = useTaskActivityStore((s) => s.markSeenLocal);
  const showRead = useTaskActivityStore((s) => s.showRead);
  const setShowRead = useTaskActivityStore((s) => s.setShowRead);
  const stickyKeys = useTaskActivityStore((s) => s.stickyKeys);

  const { registerItem, flush } = useActivitySeenTracker(scrollRef);

  const activityType = activeTab === 'notes' ? ACTIVITY_TYPE_NOTE : ACTIVITY_TYPE_COMMENT;

  // Each tab shows only its own kind, but grouping stays per task.
  //
  // Read items are hidden by default so the list stays a short "what's new"
  // rather than an ever-growing archive. Two exceptions keep that from being
  // jarring: `showRead` reveals everything, and anything in `stickyKeys` — what
  // was unread when the drawer opened, plus rows that arrived since — stays put
  // until the drawer is closed, so nothing disappears while being read.
  const visibleGroups = useMemo(
    () =>
      groups
        .map((group) => ({
          group,
          items: group.items.filter(
            (i) =>
              i.activityTypeID === activityType &&
              (showRead || !i.isSeen || stickyKeys.has(`${i.activityTypeID}:${i.itemID}`))
          ),
        }))
        .filter((g) => g.items.length > 0),
    [groups, activityType, showRead, stickyKeys]
  );

  // How many of this tab's items the read filter is currently withholding.
  const hiddenReadCount = useMemo(() => {
    if (showRead) return 0;
    return groups
      .flatMap((g) => g.items)
      .filter(
        (i) =>
          i.activityTypeID === activityType &&
          i.isSeen &&
          !stickyKeys.has(`${i.activityTypeID}:${i.itemID}`)
      ).length;
  }, [groups, activityType, showRead, stickyKeys]);

  const handleOpenTask = (taskId: number) => {
    // Flush before navigating so anything already dwelt is not lost with the
    // unmount, then let the detail page mark the rest of the task seen.
    void flush();
    setDrawerOpen(false);
    navigate(`/app/tasks/view/${taskId}?tb=${activeTab === 'notes' ? 'notes' : 'comments'}`, {
      state: { returnPath: location.pathname },
    });
  };

  // Scoped to the active tab, not the whole feed: clearing notes the user
  // cannot currently see would be a surprise, and the button sits above the
  // list it applies to.
  const handleMarkAllSeen = async () => {
    const unread = groups
      .flatMap((g) => g.items)
      .filter((i) => !i.isSeen && i.activityTypeID === activityType);

    if (unread.length === 0) return;

    markSeenLocal(unread.map((i) => `${i.activityTypeID}:${i.itemID}`));

    try {
      await feedService.markSeen(
        unread.map((i) => ({
          activityTypeID: i.activityTypeID,
          itemID: i.itemID,
          taskID: i.taskID,
        }))
      );
    } catch {
      // Idempotent server-side; the next baseline reconciles if this failed.
    }
  };

  return (
    <Drawer
      opened={drawerOpen}
      onClose={() => setDrawerOpen(false)}
      position="right"
      size="md"
      title="Activity"
    >
      {/* Pills variant with the badge flipping filled/light on the active tab —
          the same active-state treatment the task detail page uses, so the
          selection reads at a glance rather than as a faint outline. */}
      <Tabs
        value={activeTab}
        onChange={(value) => setActiveTab((value as TaskActivityTab) ?? 'comments')}
        tabsVariant="pills"
      >
        <Tabs.List grow>
          <Tabs.Tab
            value="comments"
            leftSection={<IconMessage size={16} />}
            rightSection={
              unreadComments > 0 ? (
                <Badge size="sm" circle variant={activeTab === 'comments' ? 'filled' : 'light'}>
                  {unreadComments}
                </Badge>
              ) : undefined
            }
          >
            Comments
          </Tabs.Tab>
          <Tabs.Tab
            value="notes"
            leftSection={<IconNote size={16} />}
            rightSection={
              unreadNotes > 0 ? (
                <Badge size="sm" circle variant={activeTab === 'notes' ? 'filled' : 'light'}>
                  {unreadNotes}
                </Badge>
              ) : undefined
            }
          >
            Notes
          </Tabs.Tab>
        </Tabs.List>
      </Tabs>

      {/* Rendered once, outside the panels, rather than per tab. Both tabs show
          the same component with a different filter, and a panel each would mean
          two nodes claiming the same scrollRef — only one could own the
          IntersectionObserver root, so seen-tracking broke on the other tab. */}
      <Box pt="sm">
        <ActivityList
          scrollRef={scrollRef}
          visibleGroups={visibleGroups}
          emptyLabel={
            activeTab === 'notes'
              ? "You're all caught up on notes."
              : "You're all caught up on comments."
          }
          onOpenTask={handleOpenTask}
          onMarkAllSeen={handleMarkAllSeen}
          hasUnread={activeTab === 'notes' ? unreadNotes > 0 : unreadComments > 0}
          registerItem={registerItem}
          showRead={showRead}
          onToggleShowRead={setShowRead}
          hiddenReadCount={hiddenReadCount}
        />
      </Box>
    </Drawer>
  );
}

interface ActivityListProps {
  scrollRef: React.RefObject<HTMLDivElement>;
  visibleGroups: { group: any; items: any[] }[];
  emptyLabel: string;
  hasUnread: boolean;
  onOpenTask: (taskId: number) => void;
  onMarkAllSeen: () => void;
  registerItem: ReturnType<typeof useActivitySeenTracker>['registerItem'];
  showRead: boolean;
  onToggleShowRead: (show: boolean) => void;
  hiddenReadCount: number;
}

function ActivityList({
  scrollRef,
  visibleGroups,
  emptyLabel,
  hasUnread,
  onOpenTask,
  onMarkAllSeen,
  registerItem,
  showRead,
  onToggleShowRead,
  hiddenReadCount,
}: ActivityListProps) {
  return (
    <Stack gap="sm">
      <Group justify="space-between" wrap="nowrap">
        {/* Only offered when there is something to reveal, so the control does
            not sit there implying hidden content that does not exist. */}
        {hiddenReadCount > 0 || showRead ? (
          <Switch
            size="xs"
            checked={showRead}
            onChange={(event) => onToggleShowRead(event.currentTarget.checked)}
            label={showRead ? 'Showing read' : `Show read (${hiddenReadCount})`}
          />
        ) : (
          <span />
        )}

        {hasUnread && (
          <Button
            variant="subtle"
            size="compact-xs"
            leftSection={<IconChecks size={14} />}
            onClick={onMarkAllSeen}
          >
            Mark all as read
          </Button>
        )}
      </Group>

      {/* The observer's root — items are only tracked while inside this box. */}
      <div ref={scrollRef} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        {visibleGroups.length === 0 ? (
          <Text size="sm" c="dimmed" ta="center" py="xl">
            {hiddenReadCount > 0
              ? `${emptyLabel} ${hiddenReadCount} read ${hiddenReadCount === 1 ? 'item' : 'items'} hidden.`
              : emptyLabel}
          </Text>
        ) : (
          visibleGroups.map(({ group, items }) => (
            <TaskActivityGroup
              key={group.taskID}
              group={group}
              items={items}
              unreadCount={items.filter((i: any) => !i.isSeen).length}
              onOpenTask={onOpenTask}
              registerItem={registerItem}
            />
          ))
        )}
      </div>
    </Stack>
  );
}
